export function createUploadRequests({
  config,
  uploadApiService,
  managementApiService,
  debugLog,
  getExtension,
  resolveMaxParallelPartUploads,
  waitForMilliseconds,
  multipartMaxRetryCount,
  multipartRetryDelayMs,
  uiMessages,
  fetchImpl = (...args) => fetch(...args)
}) {
  function toBase64Url(value) {
    const utf8Bytes = new TextEncoder().encode(String(value ?? ''));
    let binary = '';
    utf8Bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });

    return btoa(binary)
      .replace(/=+$/g, '')
      .replace(/\+/g, '-')
      .replace(/\//g, '_');
  }

  function buildPredictedMediaId({ applicationId, caseId, storedFileName, originalFileName }) {
    const normalizedApplicationId = String(applicationId || '').trim();
    const normalizedCaseId = String(caseId || '').trim();
    const normalizedFileName = String(storedFileName || originalFileName || '').trim();
    const seed = [normalizedApplicationId, normalizedCaseId, normalizedFileName]
      .filter(Boolean)
      .join('/');

    return seed ? `MEDIA-${toBase64Url(seed)}` : '';
  }

  async function readApiData(response, label) {
    const payload = await response.json().catch(() => ({}));

    if (!response.ok) {
      const message = payload?.error?.message || `${label} failed (${response.status})`;
      throw new Error(message);
    }

    return payload?.data ?? null;
  }

  async function postJson(path, body, label) {
    const response = await uploadApiService.request(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    return readApiData(response, label);
  }

  async function getJson(path, label) {
    const response = await uploadApiService.get(path);
    return readApiData(response, label);
  }

  async function uploadMultipartFile(file, extraMetadata, onProgress) {
    const uploadBaseUrl = String(uploadApiService?.baseUrl || '').replace(/\/$/, '');
    if (!uploadBaseUrl || !uploadApiService) {
      throw new Error(uiMessages.multipartBaseUrlMissing);
    }

    const isMediaUpload = Boolean(config.applicationNumber);
    const isInternalZoneUpload = config?.isInternalZoneUpload === true;
    const normalizedFileType = extraMetadata?.fileType || getExtension(file.name);
    const isInternalZoneMediaUpload = isInternalZoneUpload && String(normalizedFileType).toLowerCase() === 'media';
    const predictedMediaId = isInternalZoneMediaUpload
      ? buildPredictedMediaId({
        applicationId: config.clientId || 'CJTS',
        caseId: config.caseId,
        storedFileName: file.name,
        originalFileName: file.name
      })
      : '';
    debugLog('uploadMultipartFile-start', {
      name: file?.name,
      size: file?.size,
      type: file?.type,
      extraMetadata
    });
    const startResponse = await uploadApiService.startMultipartUpload({
      caseId: config.caseId,
      clientId: config.clientId || 'CJTS',
      fileName: file.name,
      fileType: normalizedFileType,
      contentType: file.type || 'application/octet-stream'
    }, {
      isMediaUpload,
      isInternalZoneUpload
    });
    const startData = await readApiData(startResponse, 'start-multipart-uploads');

    debugLog('uploadMultipartFile-start-response', {
      key: startData?.key,
      uploadId: startData?.uploadId,
      partSize: startData?.partSize,
      uploadUrl: startData?.uploadUrl ? '[present]' : '[missing]'
    });

    if (!startData?.uploadId || !startData?.key || !startData?.uploadUrl) {
      throw new Error(uiMessages.multipartStartFailed);
    }

    const partSize = Number(startData.partSize || 5 * 1024 * 1024);
    const totalParts = Math.max(1, Math.ceil(file.size / partSize));
    const uploadedParts = [];
    const uploadedPartsByNumber = new Map();
    const configuredMaxParallelPartUploads = resolveMaxParallelPartUploads(config);
    const maxParallelPartUploads = Math.min(configuredMaxParallelPartUploads, totalParts);
    let completedPartCount = 0;

    debugLog('uploadMultipartFile-parallel-config', {
      name: file?.name,
      totalParts,
      configuredMaxParallelPartUploads,
      effectiveMaxParallelPartUploads: maxParallelPartUploads,
      partSize
    });

    async function uploadSinglePart(partNumber) {
      let partUploadUrl = startData.uploadUrl;
      if (partNumber > 1) {
        const partUrlResponse = await uploadApiService.getPresignedUrl({
          key: startData.key,
          uploadId: startData.uploadId,
          partNumber
        }, {
          isMediaUpload,
          isInternalZoneUpload
        });
        const partUrlData = await readApiData(partUrlResponse, 'get-presigned-url');

        partUploadUrl = partUrlData?.uploadUrl;
      }

      if (!partUploadUrl) {
        throw new Error(uiMessages.multipartUrlMissing);
      }

      const startByte = (partNumber - 1) * partSize;
      const endByte = Math.min(startByte + partSize, file.size);
      const blobPart = file.slice(startByte, endByte);

      debugLog('uploadMultipartFile-part-start', {
        name: file?.name,
        partNumber,
        totalParts,
        sizeBytes: blobPart.size
      });

      const uploadResponse = await fetchImpl(partUploadUrl, {
        method: 'PUT',
        body: blobPart
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 part upload failed (${uploadResponse.status}).`);
      }

      const etag = uploadResponse.headers.get('ETag') || uploadResponse.headers.get('etag');
      if (!etag) {
        throw new Error(uiMessages.multipartEtagMissing);
      }

      uploadedPartsByNumber.set(partNumber, {
        partNumber,
        eTag: etag
      });
      completedPartCount += 1;

      if (typeof onProgress === 'function') {
        onProgress(Math.min(80, Math.round((completedPartCount / totalParts) * 70)));
      }

      debugLog('uploadMultipartFile-part-uploaded', {
        name: file?.name,
        partNumber,
        totalParts,
        completedPartCount,
        etag: etag ? '[present]' : '[missing]'
      });
    }

    async function uploadSinglePartWithRetry(partNumber) {
      let attempt = 0;
      while (true) {
        try {
          await uploadSinglePart(partNumber);
          return;
        } catch (error) {
          attempt += 1;
          if (attempt > multipartMaxRetryCount) {
            throw error;
          }
          debugLog('uploadMultipartFile-part-retry', {
            name: file?.name,
            partNumber,
            attempt,
            maxRetryCount: multipartMaxRetryCount,
            retryDelayMs: multipartRetryDelayMs,
            error: error?.message || 'Unknown error'
          });
          await waitForMilliseconds(multipartRetryDelayMs);
        }
      }
    }

    async function runParallelPartUploads() {
      let nextPartNumber = 1;

      async function worker() {
        while (nextPartNumber <= totalParts) {
          const currentPartNumber = nextPartNumber;
          nextPartNumber += 1;
          await uploadSinglePartWithRetry(currentPartNumber);
        }
      }

      await Promise.all(Array.from({ length: maxParallelPartUploads }, () => worker()));
    }

    await runParallelPartUploads();

    for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
      const uploadedPart = uploadedPartsByNumber.get(partNumber);
      if (!uploadedPart) {
        throw new Error(`Uploaded part metadata is missing for part ${partNumber}.`);
      }

      uploadedParts.push(uploadedPart);
    }

    debugLog('uploadMultipartFile-complete-request', {
      name: file?.name,
      key: startData.key,
      uploadId: startData.uploadId,
      fileType: normalizedFileType,
      originalFileName: file.name
    });
    const completeResponse = await uploadApiService.completeMultipartUpload({
      key: startData.key,
      uploadId: startData.uploadId,
      uploadedParts,
      caseId: config.caseId,
      fileSize: file.size,
      fileName: file.name,
      clientId: config.clientId || 'CJTS',
      applicationNumber: config.applicationNumber,
      caseTitle: config.caseTitle || '',
      uploadedByUserId: config.userId || 'web-control',
      originalFileName: file.name,
      fileType: normalizedFileType,
      mimeType: file.type || 'application/octet-stream',
      recordStatus: 'Draft',
      description: extraMetadata?.description || '',
      mediaId: extraMetadata?.mediaId || predictedMediaId || ''
    }, {
      isMediaUpload,
      isInternalZoneUpload
    });
    const completeData = await readApiData(completeResponse, 'multipart-uploads-complete');

    debugLog('uploadMultipartFile-complete-response', {
      name: file?.name,
      fileName: completeData?.fileName,
      storedFileName: completeData?.storedFileName,
      s3Key: completeData?.s3Key || completeData?.S3Key || ''
    });


    if (typeof onProgress === 'function') {
      onProgress(100);
    }

    return {
      startData,
      completeData,
      predictedMediaId
    };
  }

  async function persistMediaRecord(item, storedMediaFileName, completeData) {
    const objectKey = completeData?.s3Key || completeData?.S3Key || completeData?.objectKey || completeData?.ObjectKey || '';
    const payload = {
      mediaId: item.mediaId || completeData?.mediaId || completeData?.MediaId || '',
      caseId: config.caseId,
      applicationNumber: config.applicationNumber,
      caseTitle: config.caseTitle || '',
      uploadedByUserId: config.userId || 'web-control',
      originalFileName: item.mediaFile.name,
      storedFileName: storedMediaFileName,
      objectKey,
      fileType: 'Media',
      mimeType: item.mediaFile.type || 'application/octet-stream',
      fileSizeInBytes: item.mediaFile.size,
      dateUploadedUtc: new Date().toISOString(),
      incidentDateUtc: item.mediaMetadata?.recordedDateUtc ?? null,
      durationSeconds: item.mediaMetadata?.durationSeconds ?? null,
      description: item.description || '',
      recordStatus: 'Draft'
    };

    debugLog('persistMediaRecord-request', {
      storedMediaFileName,
      objectKey,
      originalFileName: item?.mediaFile?.name || ''
    });

    const response = await managementApiService.createMediaRecord(payload);
    return readApiData(response, 'media upsert');
  }

  async function persistTranscriptRecord(item, mediaId, storedTranscriptFileName, completeData) {
    const objectKey = completeData?.s3Key || completeData?.S3Key || completeData?.objectKey || completeData?.ObjectKey || '';
    const payload = {
      caseId: config.caseId,
      applicationNumber: config.applicationNumber,
      caseTitle: config.caseTitle || '',
      mediaId,
      originalFileName: item.transcriptFile.name,
      storedFileName: storedTranscriptFileName,
      objectKey,
      mimeType: item.transcriptFile.type || 'application/octet-stream',
      fileSizeInBytes: item.transcriptFile.size,
      dateUploadedUtc: new Date().toISOString(),
      description: item.transcriptDescription || ''
    };

    debugLog('persistTranscriptRecord-request', {
      storedTranscriptFileName,
      objectKey,
      mediaId
    });

    const response = await managementApiService.createTranscriptRecord(payload);

    return readApiData(response, 'transcript upsert');
  }

  return {
    postJson,
    uploadMultipartFile,
    persistMediaRecord,
    persistTranscriptRecord
  };
}
